import { useState, useEffect, useRef, useCallback } from 'react';
import { Send } from 'lucide-react';
import { colors } from '../constants/colors';
import { useAuth } from '../contexts/AuthContext';
import { useStreamChat } from '../contexts/StreamContext';
import { useLiveChatMessages } from '../hooks/useLiveChatMessages';
import { UserAvatar } from './UserAvatar';
import { UserActionMenu } from './UserActionMenu';

interface ChatProps {
  showId?: string;
  channelId: string | null;
  sellerId?: string;
  /** When set, caps the messages area at this height. When omitted, the chat
   *  fills its parent container (which must define a height). */
  maxHeight?: number;
}

interface SelectedUser {
  id: string;
  name: string;
  anchorTop: number;
  anchorLeft: number;
}



export function Chat({ showId, channelId, sellerId, maxHeight }: ChatProps) {
  const { isReady } = useStreamChat();
  const { user } = useAuth();
  const { messages, isJoined, sendMessage } = useLiveChatMessages(channelId, showId);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);

  const isSeller = !!(user && sellerId && user.id === sellerId);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleNameClick = useCallback((e: React.MouseEvent, userId: string | null, userName: string) => {
    if (!userId || !user) return;
    if (userId === user.id) return; // Don't show menu for own messages

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;

    setSelectedUser({
      id: userId,
      name: userName,
      anchorTop: rect.bottom - containerRect.top,
      anchorLeft: rect.left - containerRect.left,
    });
  }, [user]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    await sendMessage(inputValue.trim());
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!channelId || !isReady) {
    return (
      <div style={styles.container}>
        <div style={styles.disconnected}>
          <p style={styles.disconnectedText}>
            {!channelId ? 'Chat not available for this show' : 'Connecting to chat...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} ref={containerRef}>
      <div style={{ ...styles.messagesContainer, ...(maxHeight !== undefined ? { maxHeight } : {}), position: 'relative' }}>
        {messages.length === 0 ? (
          <p style={styles.emptyText}>Be the first to say something...</p>
        ) : (
          messages.map(msg => (
            <div key={msg.id} style={styles.message}>
              <UserAvatar src={msg.senderAvatar} alt={msg.senderName} size={28} />
              <div style={styles.messageContent}>
                <div style={styles.senderRow}>
                  <span
                    style={{
                      ...styles.senderName,
                      color: msg.isLocal ? '#4F46E5' : colors.ink.black,
                      cursor: msg.isLocal ? 'default' : 'pointer',
                    }}
                    onClick={(e) => !msg.isLocal && handleNameClick(e, msg.senderId, msg.senderName)}
                  >
                    {msg.isLocal ? 'You' : msg.senderName}
                  </span>
                  {sellerId && msg.senderId === sellerId && (
                    <span style={styles.hostPill}>Host</span>
                  )}
                </div>
                <span style={styles.messageText}>{msg.content}</span>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />

        <UserActionMenu
          user={selectedUser ? { id: selectedUser.id, name: selectedUser.name } : null}
          position={selectedUser ? { top: selectedUser.anchorTop, left: selectedUser.anchorLeft } : { top: 0, left: 0 }}
          isSeller={isSeller}
          showId={showId}
          onClose={() => setSelectedUser(null)}
        />
      </div>

      <div style={styles.inputContainer}>
        <div className="chat-input-pill" style={styles.inputPill}>
          <input
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Say something..."
            style={styles.input}
            disabled={!isJoined}
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || !isJoined}
            style={{ ...styles.sendButton, opacity: inputValue.trim() && isJoined ? 1 : 0.5 }}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
    position: 'relative',
    flex: 1,
    minHeight: 0,
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px',
  },
  emptyText: {
    color: colors.gray[400],
    fontSize: '14px',
    textAlign: 'center',
    margin: '20px 0',
  },
  message: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '12px',
  },
  avatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.white,
    fontSize: '12px',
    fontWeight: '600',
    flexShrink: 0,
  },
  avatarImg: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
  },
  messageContent: {
    flex: 1,
    minWidth: 0,
  },
  senderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '2px',
  },
  senderName: {
    fontWeight: '600',
    fontSize: '13px',
  },
  hostPill: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#fff',
    background: colors.accent.pink,
    padding: '1px 6px',
    borderRadius: 999,
    letterSpacing: '0.3px',
  },
  messageText: {
    display: 'block',
    fontSize: '14px',
    color: '#374151',
    wordBreak: 'break-word',
    lineHeight: 1.4,
  },
  inputContainer: {
    padding: '8px 12px',
    borderTop: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.white,
  },
  inputPill: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '24px',
    padding: '4px 6px 4px 16px',
  },
  input: {
    flex: 1,
    padding: '10px 0',
    fontSize: '14px',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: colors.ink.black,
  },
  sendButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    backgroundColor: 'transparent',
    color: colors.gray[400],
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    flexShrink: 0,
  },
  disconnected: {
    padding: '24px',
    textAlign: 'center',
  },
  disconnectedText: {
    color: colors.gray[400],
    fontSize: '14px',
  },
};
